import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointAssetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { JointAssetsLiabilitiesPartThreeFormData } from "app/utils/types";
import DynamicJointExistingMortgage from "app/components/Individual/applicant/assetsLiabilities/DynamicJointExistingMortgage";
import { useDeleteItem } from "app/hooks/useDeleteItem";

const PartThree: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );
  const { mutateAsync: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();

  const { formThree, assetLiabilityId, secoundAssetLiabilityId, ...others } =
    useSelector(
      (state: RootState) => state.jointAssetsLiabilities.stepsData
    ) || {
      formThree: { existingMortgage: [] },
    };
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<JointAssetsLiabilitiesPartThreeFormData>({
    defaultValues: {
      yourDetails: {
        existingMortgageType: "no",
        existingMortgage: [
          {
            lenderName: "",
            balance: "",
            availableRedraw: "",
            minMonthlyPayment: "",
            loanTerms: "",
            interestrate: "",
            noOfTitleHolders: "",
            propertyAddress: "",
            isInvestmentLoan: false,
            toBeRefinanced: false,
          },
        ],
      },
      coBorrowerDetails: {
        existingMortgageType: "no",
        existingMortgage: [
          {
            lenderName: "",
            balance: "",
            availableRedraw: "",
            minMonthlyPayment: "",
            loanTerms: "",
            interestrate: "",
            noOfTitleHolders: "",
            propertyAddress: "",
            isInvestmentLoan: false,
            toBeRefinanced: false,
          },
        ],
      },
    },
  });

  const existingMortgageValue = watch("yourDetails.existingMortgageType");
  const coExistingMortgageValue = watch(
    "coBorrowerDetails.existingMortgageType"
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: "yourDetails.existingMortgage",
  });

  const {
    fields: coExistingMortgageFields,
    append: coExistingMortgageAppend,
    remove: coExistingMortgageRemove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.existingMortgage",
  });

  const onSubmit = async (data: JointAssetsLiabilitiesPartThreeFormData) => {
    const { yourDetails, coBorrowerDetails } = data;

    const yourDetailsPayload = {
      existingMortgage:
        yourDetails.existingMortgageType === "yes"
          ? yourDetails?.existingMortgage?.map((item) => ({
              ...(item.dbId && { id: item.dbId }),
              lenderName: item.lenderName,
              interestrate: item.interestrate,
              propertyAddress: item.propertyAddress,
              isInvestmentLoan: item.isInvestmentLoan,
              toBeRefinanced: item.toBeRefinanced,
              balance: Number(item.balance),
              availableRedraw: Number(item.availableRedraw),
              minMonthlyPayment: Number(item.minMonthlyPayment),
              loanTerms: Number(item.loanTerms),
              noOfTitleHolders: Number(item.noOfTitleHolders),
            }))
          : null,
    };
    const coBorrowerDetailsPayload = {
      existingMortgage:
        coBorrowerDetails.existingMortgageType === "yes"
          ? coBorrowerDetails?.existingMortgage?.map((item) => ({
              ...(item.dbId && { id: item.dbId }),
              lenderName: item.lenderName,
              interestrate: item.interestrate,
              propertyAddress: item.propertyAddress,
              isInvestmentLoan: item.isInvestmentLoan,
              toBeRefinanced: item.toBeRefinanced,
              balance: Number(item.balance),
              availableRedraw: Number(item.availableRedraw),
              minMonthlyPayment: Number(item.minMonthlyPayment),
              loanTerms: Number(item.loanTerms),
              noOfTitleHolders: Number(item.noOfTitleHolders),
            }))
          : null,
    };

    const request1 = {
      url: `${ASSST_LIABILITY}/${savedIds.applicantID}/third`,
      data: yourDetailsPayload,
    };
    const request2 = {
      url: `${ASSST_LIABILITY}/${savedIds.secondApplicantID}/third`,
      data: coBorrowerDetailsPayload,
    };
    try {
      const [response1, response2] = await Promise.all([
        submitAssetsLiabilities(request1),
        submitAssetsLiabilities(request2),
      ]);
      if (response1 && response2) {
        const yourExistingMortgageMapped = response1?.existingMortgage?.map(
          (item: any) => ({
            dbId: item.id,
            lenderName: item.lenderName,
            balance: item.balance,
            availableRedraw: item.availableRedraw,
            minMonthlyPayment: item.minMonthlyPayment,
            loanTerms: String(item.loanTerms),
            interestrate: item.interestrate,
            noOfTitleHolders: String(item.noOfTitleHolders),
            propertyAddress: item.propertyAddress,
            isInvestmentLoan: item.isInvestmentLoan,
            toBeRefinanced: item.toBeRefinanced,
          })
        );

        const coExistingMortgageMapped = response2?.existingMortgage?.map(
          (item: any) => ({
            dbId: item.id,
            lenderName: item.lenderName,
            balance: item.balance,
            availableRedraw: item.availableRedraw,
            minMonthlyPayment: item.minMonthlyPayment,
            loanTerms: String(item.loanTerms),
            interestrate: item.interestrate,
            noOfTitleHolders: String(item.noOfTitleHolders),
            propertyAddress: item.propertyAddress,
            isInvestmentLoan: item.isInvestmentLoan,
            toBeRefinanced: item.toBeRefinanced,
          })
        );
        const savedData = {
          yourDetails: {
            ...formThree?.yourDetails,
            existingMortgage:
              yourDetails.existingMortgageType === "no"
                ? []
                : yourExistingMortgageMapped,
            existingMortgageType: yourDetails.existingMortgageType,
          },
          coBorrowerDetails: {
            ...formThree?.coBorrowerDetails,
            existingMortgage:
              coBorrowerDetails.existingMortgageType === "no"
                ? []
                : coExistingMortgageMapped,
            existingMortgageType: coBorrowerDetails.existingMortgageType,
          },
        };
        dispatch(
          addStepsData({
            ...others,
            formThree: savedData,
          })
        );
        dispatch(nextStep());
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formThree && Object.keys(formThree).length > 0) {
      reset({
        yourDetails: {
          ...formThree.yourDetails,

          existingMortgage:
            formThree.yourDetails?.existingMortgageType === "no"
              ? [
                  {
                    lenderName: "",
                    balance: "",
                    availableRedraw: "",
                    minMonthlyPayment: "",
                    loanTerms: "",
                    interestrate: "",
                    noOfTitleHolders: "",
                    propertyAddress: "",
                    isInvestmentLoan: false,
                    toBeRefinanced: false,
                  },
                ]
              : formThree.yourDetails?.existingMortgage?.map((item: any) => ({
                  ...item,
                  interestrate: String(item?.interestrate ?? ""),
                  noOfTitleHolders: String(item?.noOfTitleHolders ?? ""),
                  loanTerms: String(item?.loanTerms ?? ""),
                })) || [
                  {
                    lenderName: "",
                    balance: "",
                    availableRedraw: "",
                    minMonthlyPayment: "",
                    loanTerms: "",
                    interestrate: "",
                    noOfTitleHolders: "",
                    propertyAddress: "",
                    isInvestmentLoan: false,
                    toBeRefinanced: false,
                  },
                ],
        },
        coBorrowerDetails: {
          ...formThree.coBorrowerDetails,
          existingMortgage:
            formThree.coBorrowerDetails?.existingMortgageType === "no"
              ? [
                  {
                    lenderName: "",
                    balance: "",
                    availableRedraw: "",
                    minMonthlyPayment: "",
                    loanTerms: "",
                    interestrate: "",
                    noOfTitleHolders: "",
                    propertyAddress: "",
                    isInvestmentLoan: false,
                    toBeRefinanced: false,
                  },
                ]
              : formThree.coBorrowerDetails.existingMortgage?.map(
                  (item: any) => ({
                    ...item,
                    interestrate: String(item?.interestrate ?? ""),
                    noOfTitleHolders: String(item?.noOfTitleHolders ?? ""),
                    loanTerms: String(item?.loanTerms ?? ""),
                  })
                ) || [
                  {
                    lenderName: "",
                    balance: "",
                    availableRedraw: "",
                    minMonthlyPayment: "",
                    loanTerms: "",
                    interestrate: "",
                    noOfTitleHolders: "",
                    propertyAddress: "",
                    isInvestmentLoan: false,
                    toBeRefinanced: false,
                  },
                ],
        },
      });
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={36} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Existing Mortgages" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-8 flex-col md:flex-col xl:flex-row sm:gap-4">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any existing mortgage?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.existingMortgageType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="existingmortgage_yes"
                          name="yourDetails.existingMortgageType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="existingmortgage_no"
                          name="yourDetails.existingMortgageType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.existingMortgageType && (
                  <ErrorsMessage
                    title={errors?.yourDetails?.existingMortgageType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {existingMortgageValue === "yes" && (
                <>
                  <DynamicJointExistingMortgage
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    fields={fields}
                    deleteItem={deleteItem}
                    existingMortgage="yourDetails.existingMortgage"
                    applicantID={savedIds?.applicantID as string}
                    remove={remove}
                    deleteIsPending={deleteIsPending}
                  />

                  {fields.length < 3 && (
                    <div className="flex items-center justify-end mt-6 mb-4 sm:mb-2">
                      <AddMore
                        onClick={() =>
                          append({
                            lenderName: "",
                            balance: "",
                            availableRedraw: "",
                            minMonthlyPayment: "",
                            loanTerms: "",
                            interestrate: "",
                            noOfTitleHolders: "",
                            propertyAddress: "",
                            isInvestmentLoan: false,
                            toBeRefinanced: false,
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />

              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any existing mortgage?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.existingMortgageType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="coexistingmortgage_yes"
                          name="coBorrowerDetails.existingMortgageType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="coBorrowerDetails.existingMortgageType"
                          name="coexistingmortgage"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.existingMortgageType && (
                  <ErrorsMessage
                    title={
                      errors?.coBorrowerDetails.existingMortgageType.message
                    }
                    className="!text-left"
                  />
                )}
              </div>

              {coExistingMortgageValue === "yes" && (
                <>
                  <DynamicJointExistingMortgage
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    fields={coExistingMortgageFields}
                    deleteItem={deleteItem}
                    existingMortgage="coBorrowerDetails.existingMortgage"
                    applicantID={savedIds?.secondApplicantID as string}
                    remove={coExistingMortgageRemove}
                    deleteIsPending={deleteIsPending}
                  />
                  {coExistingMortgageFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6 mb-4 sm:mb-2">
                      <AddMore
                        onClick={() =>
                          coExistingMortgageAppend({
                            lenderName: "",
                            balance: "",
                            availableRedraw: "",
                            minMonthlyPayment: "",
                            loanTerms: "",
                            interestrate: "",
                            noOfTitleHolders: "",
                            propertyAddress: "",
                            isInvestmentLoan: false,
                            toBeRefinanced: false,
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartThree;
